"use client";
import { Button } from "@cmp-src/primitives/button";
import { PlusIcon } from "lucide-react";
import React from "react";
import { MULTI_ROOM_RESERVATION_MAX_ROOMS } from "@/features/reserve/multiRoomReservation.config";
import { useRoomSelection } from "@/features/reserve/providers/roomSelectionContext";

const classes = {
  Button: "shrink-0 gap-1.5",
  Icon: "size-4"
};

export interface AddAnotherRoomButtonProps {
  /** Runs just before the map is armed, so the caller can clear what covers it. */
  onStartPicking?: () => void;
}

/**
 * Arms the map to pick one more room for the reservation being composed.
 */
export const AddAnotherRoomButton: React.FC<AddAnotherRoomButtonProps> = ({
  onStartPicking
}) => {
  const { isPickingRoom, canAddRoom, startPicking, cancelPicking } =
    useRoomSelection();

  const handleStartPicking = React.useCallback(() => {
    onStartPicking?.();
    startPicking();
  }, [onStartPicking, startPicking]);

  if (isPickingRoom) {
    return (
      <Button
        type="button"
        variant="outline"
        className={classes.Button}
        onClick={cancelPicking}
      >
        Picking…
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      className={classes.Button}
      onClick={handleStartPicking}
      disabled={!canAddRoom}
      title={
        canAddRoom
          ? undefined
          : `A reservation can hold up to ${MULTI_ROOM_RESERVATION_MAX_ROOMS} rooms.`
      }
    >
      <PlusIcon className={classes.Icon} />
      Add another room
    </Button>
  );
};
